import { defineMessages, type MessageDescriptor } from 'react-intl';

export type Errors =
  | AgentUnavailableError
  | NoAccessError
  | NoScopesError
  | NoSitesError
  | UnauthorizedScopesError
  | UnexpectedError;

export type UnauthorizedScopesError = {
  error: 'unauthorized-scopes';
  clientName: string;
  scopes: string[];
  products: string[];
};

export type NoScopesError = {
  error: 'no-scopes';
  clientName: string;
  /** The site URL the user selected when the error was triggered */
  siteName?: string;
};

export type NoSitesError = {
  error: 'no-sites';
  product: string;
};

export type NoAccessError = {
  error: 'no-access';
};

/** The Rovo Agent named by an Agent MCP consent could not be loaded. */
export type AgentUnavailableError = {
  error: 'agent-unavailable';
  clientName?: string;
};

export type UnexpectedError = {
  error: 'unexpected';
  clientName?: string;
  description: string | MessageDescriptor;
};

export function isError(error: any): error is Errors {
  // If error is not null/undefined, is an object, and has an "error" field string
  return error && typeof error === 'object' && typeof error.error === 'string';
}

const errorMessages = defineMessages({
  noContextToken: {
    id: 'error.noContextToken.description',
    defaultMessage: 'No context provided. Please try authorizing again.',
    description:
      'The user does not have a context token which means they probably did not land on this site from the expected flow. The user should try going through the authorize flow again.',
  },
  invalidContextToken: {
    id: 'error.invalidContextToken.description',
    defaultMessage: 'Invalid context provided. Please try authorizing again.',
    description:
      'The context used is not a valid context. The user should try going through the authorize flow again.',
  },
  expiredContextToken: {
    id: 'error.expiredContextToken.description',
    defaultMessage: 'The session has expired. Please try authorizing again.',
    description:
      'The session has taken too long and so it has expired. The user should try going through the authorize flow again.',
  },
});

export const NoContextTokenError: UnexpectedError = {
  error: 'unexpected',
  description: errorMessages.noContextToken,
};

export const InvalidContextToken: UnexpectedError = {
  error: 'unexpected',
  description: errorMessages.invalidContextToken,
};

export const ExpiredContextTokenError: UnexpectedError = {
  error: 'unexpected',
  description: errorMessages.expiredContextToken,
};

export class HttpError extends Error {
  status: number;

  constructor(status: number, message?: string, options?: ErrorOptions) {
    super(message, options);
    this.status = status;
  }
}

export const noAccessError: NoAccessError = {
  error: 'no-access',
};
